import _get from 'lodash.get';

import { normalizeAdyenCardBrand } from '@components/AdyenCreditCardForm/AdyenCreditCardForm.static';
import { CreditCardBrand, Nullable } from '@types-internal';
import { defaultString } from '@utils/utils';

export interface CardErrors {
  encryptedCardNumber: Nullable<string>;
  encryptedExpiryDate: Nullable<string>;
  encryptedSecurityCode: Nullable<string>;
  encryptedExpiryMonth?: Nullable<string>;
  encryptedExpiryYear?: Nullable<string>;
}

export interface AllErrors extends CardErrors {
  holderName: Nullable<string>;
}

export type AdyenStateErrorMessages = {
  requiredNameMessage: string;
  requiredMonthMessage: string;
  requiredCVVMessage: string;
  requiredNumberMessage: string;
  requiredYearMessage: string;
};

export interface ParsedAdyenPaymentMethod {
  type: string;
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode: string;
}

export interface ParsedAdyenState {
  isValid: boolean;
  paymentMethod: ParsedAdyenPaymentMethod;
  errors: CardErrors;
}

export interface AdyenState {
  isValid: boolean;
  storeInVault: boolean;
  creditCardBrand: Nullable<CreditCardBrand>;
  lastFour: string;

  // This field corresponds to the 'paymentMethod' of the state object from the 'onChange' callback.
  // It also corresponds to the 'paymentMethod' field needed to start a transaction with Adyen's enpoint /payments
  // See the request example here: https://docs.adyen.com/api-explorer/Checkout/69/post/payments#examples-request-card-3d-secure-securedfields
  paymentMethod: {
    type: string;
    encryptedCardNumber: string;
    encryptedExpiryMonth: string;
    encryptedExpiryYear: string;
    encryptedSecurityCode: string;
    holderName: string;
  };
}

export interface AdyenCreditCardFormData {
  type: string;
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode: string;
  holderName: string;
  storeInVault: boolean;
  creditCardBrand: Nullable<CreditCardBrand>;
  lastFour: string;
}

/*
 * Errors
 */

export const DEFAULT_CARD_ERRORS: CardErrors = {
  encryptedCardNumber: null,
  encryptedExpiryDate: null,
  encryptedExpiryMonth: null,
  encryptedExpiryYear: null,
  encryptedSecurityCode: null,
};

export const DEFAULT_ERRORS: AllErrors = {
  ...DEFAULT_CARD_ERRORS,
  holderName: null,
};

/**
 * Validate the credit card holder name with the following rules:
 *  - The name cannot be empty
 *  - Length between 4 and 30 included
 *
 * @param holderName The string value that needs to be validated
 * @return True if the given name is valid, otherwise false
 */
export const isHolderNameValid = (holderName: string): boolean => {
  /* Regex explanation: String can contain any character except
   * linebreaks. Length between 4 and 30 characters included. */
  const regex = /^.{4,30}$/;
  return regex.test(holderName);
};

export function maybeErrorForHolderName(
  holderName: string,
  t: (key: string) => string,
  isModernize = false,
): Nullable<string> {
  if (holderName === '') {
    return t('checkout.form.required.error');
  } else if (!isModernize && !isHolderNameValid(holderName)) {
    return t('checkout.payment.creditcard.name.invalid');
  } else {
    return null;
  }
}

export function areIdenticalCardErrors(currentErrors: CardErrors, newErrors: CardErrors) {
  return (
    currentErrors.encryptedCardNumber === newErrors.encryptedCardNumber &&
    currentErrors.encryptedExpiryDate === newErrors.encryptedExpiryDate &&
    currentErrors.encryptedExpiryMonth === newErrors.encryptedExpiryMonth &&
    currentErrors.encryptedExpiryYear === newErrors.encryptedExpiryYear &&
    currentErrors.encryptedSecurityCode === newErrors.encryptedSecurityCode
  );
}

/*
 * Parsing Adyen's state in the 'onChange' callback
 */

const DEFAULT_ON_CHANGE_PAYMENT_METHOD = {
  type: '',
  encryptedCardNumber: '',
  encryptedExpiryMonth: '',
  encryptedExpiryYear: '',
  encryptedSecurityCode: '',
};

export const DEFAULT_ADYEN_STATE: AdyenState = {
  isValid: false,
  storeInVault: false,
  creditCardBrand: null,
  lastFour: '',
  paymentMethod: {
    ...DEFAULT_ON_CHANGE_PAYMENT_METHOD,
    holderName: '',
  },
};

export function getPaymentMethodField(stateOnChange: unknown, name: string) {
  return defaultString(_get(stateOnChange, `data.paymentMethod.${name}`));
}

export function getErrorField(stateOnChange: unknown, name: string) {
  const value = _get(stateOnChange, `errors.${name}.errorI18n`);
  return typeof value === 'string' ? value : null;
}

// This extracts the data we need from the stateOnChange from Adyen, and uses to the appropriate default values for each field.
export function parseStateOnChange(stateOnChange: unknown): ParsedAdyenState {
  return {
    isValid: _get(stateOnChange, 'isValid', false),
    paymentMethod: {
      type: getPaymentMethodField(stateOnChange, 'type'),
      encryptedCardNumber: getPaymentMethodField(stateOnChange, 'encryptedCardNumber'),
      encryptedExpiryMonth: getPaymentMethodField(stateOnChange, 'encryptedExpiryMonth'),
      encryptedExpiryYear: getPaymentMethodField(stateOnChange, 'encryptedExpiryYear'),
      encryptedSecurityCode: getPaymentMethodField(stateOnChange, 'encryptedSecurityCode'),
    },
    errors: {
      encryptedCardNumber: getErrorField(stateOnChange, 'encryptedCardNumber'),
      encryptedSecurityCode: getErrorField(stateOnChange, 'encryptedSecurityCode'),
      // The month field and the date field are on the same height in the UI. Only one error message can be displayed.
      // => we pick the first non-null error, priority (arbitrarily) given to the month field.
      encryptedExpiryMonth: getErrorField(stateOnChange, 'encryptedExpiryMonth'),
      encryptedExpiryYear: getErrorField(stateOnChange, 'encryptedExpiryYear'),
      encryptedExpiryDate:
        getErrorField(stateOnChange, 'encryptedExpiryMonth') ??
        getErrorField(stateOnChange, 'encryptedExpiryYear'),
    },
  };
}

export function isAdyenStateValid(state: AdyenState): boolean {
  return (
    state.isValid &&
    state.paymentMethod.type !== '' &&
    state.paymentMethod.encryptedCardNumber !== '' &&
    state.paymentMethod.encryptedExpiryMonth !== '' &&
    state.paymentMethod.encryptedExpiryYear !== '' &&
    state.paymentMethod.encryptedSecurityCode !== '' &&
    state.paymentMethod.holderName !== ''
  );
}

export function convertAdyenState(state: AdyenState): AdyenCreditCardFormData {
  return {
    ...state.paymentMethod,
    storeInVault: state.storeInVault,
    creditCardBrand: state.creditCardBrand,
    lastFour: state.lastFour,
  };
}

export const setupRequiredMessages = (
  errors: AllErrors,
  state: AdyenState,
  requiredMessages: AdyenStateErrorMessages,
  newErrors: Partial<AllErrors> = {},
) => {
  if (errors.encryptedCardNumber === null && state.paymentMethod.encryptedCardNumber === '') {
    newErrors.encryptedCardNumber = requiredMessages.requiredNumberMessage;
  }

  if (errors.holderName === null && state.paymentMethod.holderName === '') {
    newErrors.holderName = requiredMessages.requiredNameMessage;
  }

  if (errors.encryptedExpiryMonth === null && state.paymentMethod.encryptedExpiryMonth === '') {
    newErrors.encryptedExpiryMonth = requiredMessages.requiredMonthMessage;
  }

  if (errors.encryptedExpiryYear === null && state.paymentMethod.encryptedExpiryYear === '') {
    newErrors.encryptedExpiryYear = requiredMessages.requiredYearMessage;
  }
};

// We have a field error from Adyen only if the customer actually touched the related input.
// So 'no error' means that either the field is valid or empty: if empty, we had the requiredMessage.
export function addRequiredMessageIfNeeded(
  errors: AllErrors,
  state: AdyenState,
  requiredMessages: AdyenStateErrorMessages | string,
): Partial<AllErrors> {
  const newErrors: Partial<AllErrors> = {};

  if (typeof requiredMessages === 'string') {
    if (
      errors.encryptedExpiryDate === null &&
      (state.paymentMethod.encryptedExpiryMonth === '' ||
        state.paymentMethod.encryptedExpiryYear === '')
    ) {
      newErrors.encryptedExpiryDate = requiredMessages;
    }
    requiredMessages = {
      requiredNameMessage: requiredMessages,
      requiredMonthMessage: requiredMessages,
      requiredCVVMessage: requiredMessages,
      requiredNumberMessage: requiredMessages,
      requiredYearMessage: requiredMessages,
    };
  }

  setupRequiredMessages(errors, state, requiredMessages, newErrors);

  if (errors.encryptedSecurityCode === null && state.paymentMethod.encryptedSecurityCode === '') {
    newErrors.encryptedSecurityCode = requiredMessages.requiredCVVMessage;
  }

  return newErrors;
}

/*
 * Other parsing utilities
 */
export function getLastFourDigits(data: unknown): Nullable<string> {
  if (_get(data, 'fieldType') === 'encryptedCardNumber') {
    const endDigits = _get(data, 'endDigits');
    return typeof endDigits === 'string' || typeof endDigits === 'number' ? String(endDigits) : '';
  }

  return null;
}

export function getFromAdyenCardBrand(brandData: unknown): Nullable<CreditCardBrand> {
  return normalizeAdyenCardBrand(_get(brandData, 'brand', brandData));
}

export function getFocusedElement(data: unknown): Nullable<string> {
  const focusedElement = _get(data, 'currentFocusObject');
  return typeof focusedElement === 'string' ? focusedElement : null;
}
